// ImportarProdutos.jsx
import React, { useEffect, useState } from "react";
import { Card, Button, Typography, notification, Spin } from "antd";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { useConfigQuery } from "../../../../queries/useConfig";
import axiosInstance from "../../../../axiosInterceptor";
import {
  cancelarTransacaoPendente,
  gerarTransacao,
  getTransacaoById,
} from "../../../Services/Funcoes";
import { api } from "../../../../config/api";

const { Title } = Typography;

/* ===== estilos ===== */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  min-height: 100vh;
  background: #f0f2f5;
`;
const UploadCard = styled(Card)`
  width: 600px;
  margin-top: 50px;
  padding: 24px;
`;

/* Área de arrastar/soltar */
const DropZone = styled.div`
  margin-top: 12px;
  border: 2px dashed ${({ $dragging }) => ($dragging ? "#1677ff" : "#bbb")};
  background: ${({ $dragging }) =>
    $dragging ? "rgba(22, 119, 255, 0.06)" : "#fff"};
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  color: #555;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  &:hover {
    border-color: #1677ff;
  }
`;

/* ===== colunas e mapeamento de cabeçalhos =====
   - REQUIRED_COLS: obrigatórias (se faltar, dá erro)
   - OPTIONAL_COLS: opcionais (se faltar, aplica default)
*/
const REQUIRED_COLS = [
  { key: "id", header: "id" },
  { key: "nome", header: "nome" },
  { key: "descricao", header: "descricao" },
  { key: "preco", header: "preco" },
  { key: "custo", header: "custo" },
  { key: "codigo", header: "codigo" },
  { key: "ean", header: "ean" },
  { key: "unidade", header: "unidade" },
  { key: "categoria", header: "nome da categoria" },
  { key: "ncm", header: "ncm" },
  { key: "cfop", header: "cfop" },
  { key: "origem", header: "origem" },

  // ✅ CSTs como string (sem letras/símbolos) - obrigatórios
  { key: "cst", header: "cst" },
  { key: "cstPis", header: "cstPis" },
  { key: "cstCofins", header: "cstCofins" },

  { key: "picms", header: "aliquota_icms" },
  { key: "predBc", header: "reducao_icms" },

  // CST IPI (você pode tratar como opcional)
  { key: "cstIpi", header: "cstIpi" },

  { key: "pipi", header: "aliquota_ipi" },
  { key: "ppis", header: "aliquota_pis" },
  { key: "pcofins", header: "aliquota_cofins" },
];

const OPTIONAL_COLS = [
  { key: "unidadeTributavel", header: "unidade_tributavel" },
  { key: "ibscbsCst", header: "ibscbs_cst" }, // ✅ string, aceita zeros como vier
  { key: "cclassTrib", header: "cclass_trib" }, // ✅ string, aceita zeros como vier
  { key: "pibsuf", header: "pibsuf" },
  { key: "pibsMun", header: "pibsmun" },
  { key: "pcbs", header: "pcbs" },
];

const COLS = [...REQUIRED_COLS, ...OPTIONAL_COLS];

const headerToKeyBase = Object.fromEntries(
  COLS.map((c) => [String(c.header).trim().toLowerCase(), c.key])
);

const HEADER_ALIASES = {
  // ========= CSTs =========
  "cst pis": "cstPis",
  "cst_pis": "cstPis",
  cstpis: "cstPis",

  "cst cofins": "cstCofins",
  "cst_cofins": "cstCofins",
  cstcofins: "cstCofins",

  "cst ipi": "cstIpi",
  "cst_ipi": "cstIpi",
  cstipi: "cstIpi",

  // ========= PIS/COFINS alíquotas =========
  "p pis": "ppis",
  "p_pis": "ppis",
  "aliquota pis": "ppis",
  aliquota_pis: "ppis",

  "p cofins": "pcofins",
  "p_cofins": "pcofins",
  "aliquota cofins": "pcofins",
  aliquota_cofins: "pcofins",

  // ========= ICMS =========
  predbc: "predBc",
  "pre dbc": "predBc",
  p_red_bc: "predBc",
  reducao_icms: "predBc",

  "aliquota icms": "picms",
  aliquota_icms: "picms",
  p_icms: "picms",

  // ========= categoria =========
  categoria: "categoria",
  "nome da categoria": "categoria",

  // ✅ novos aliases (IBSCBS)
  "unidade tributavel": "unidadeTributavel",
  unidade_tributavel: "unidadeTributavel",
  unidadetributavel: "unidadeTributavel",

  "ibscbs cst": "ibscbsCst",
  ibscbs_cst: "ibscbsCst",
  ibscbscst: "ibscbsCst",

  "cclass trib": "cclassTrib",
  cclass_trib: "cclassTrib",
  cclasstrib: "cclassTrib",

  pibsuf: "pibsuf",
  "pibs uf": "pibsuf",
  p_ibsuf: "pibsuf",

  pibsmun: "pibsMun",
  "pibs mun": "pibsMun",
  p_ibsmun: "pibsMun",

  pcbs: "pcbs",
};

function mapHeaderToKey(rawHeader) {
  const norm = String(rawHeader || "").trim().toLowerCase();
  return headerToKeyBase[norm] || HEADER_ALIASES[norm] || null;
}

/* ===== utils ===== */
function parseNumberBR(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number") return value;
  const s = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isNaN(n) ? value : n;
}

function linhaVaziaByHeaders(rowArr, headers) {
  for (let i = 0; i < headers.length; i++) {
    const v = rowArr[i];
    if (v != null && String(v).trim() !== "") return false;
  }
  return true;
}

function normalizarTiposNumericos(objApi) {
  const out = { ...objApi };
  // ✅ inclui os novos campos numéricos
  const numKeys = [
    "preco",
    "custo",
    "picms",
    "predBc",
    "pipi",
    "ppis",
    "pcofins",
    "pibsuf",
    "pibsMun",
    "pcbs",
  ];
  numKeys.forEach((k) => (out[k] = parseNumberBR(out[k])));
  return out;
}

function productLabel(o, idx) {
  const nome = (o?.nome || "").toString().trim();
  if (nome) return nome;
  const codigo = (o?.codigo || "").toString().trim();
  if (codigo) return `cód ${codigo}`;
  if (o?.id) return `id ${o.id}`;
  return `linha ${idx + 2}`;
}

/**
 * ✅ Para CSTs / códigos:
 * - aceita exatamente como veio (com zeros, se vier como texto)
 * - garante apenas dígitos
 * - retorna string
 * - se vier vazio: retorna default (se required=true, retorna null)
 *
 * Observação: se o Excel entregar número (ex: 1), zeros à esquerda já foram perdidos na origem.
 */
function sanitizeDigitsKeepAsIs(value, defaultValue = "", required = false) {
  if (value == null) return required ? null : defaultValue;

  if (typeof value === "number" && Number.isFinite(value)) {
    const s = String(Math.trunc(value));
    if (!s) return required ? null : defaultValue;
    return s;
  }

  const s = String(value).trim();
  if (!s) return required ? null : defaultValue;

  if (!/^\d+$/.test(s)) return null; // não aceita letra/símbolo

  return s; // mantém zeros como veio
}

/* ===== validação/ajustes ===== */
function validarEAjustar(items) {
  const errors = [];
  const ajustados = items.map((item, idx) => {
    const o = { ...item };
    const label = productLabel(o, idx);
    const row = idx + 2;

    if (!o.nome || String(o.nome).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): nome não pode ser em branco.`);
    }
    if (!o.descricao || String(o.descricao).trim() === "") {
      o.descricao = o.nome || "";
    }

    const precoNum = Number(o.preco);
    if (o.preco === "" || !Number.isFinite(precoNum) || precoNum <= 0) {
      errors.push(`Linha ${row} (Produto: ${label}): preço deve ser numérico e maior que zero.`);
    } else {
      o.preco = precoNum;
    }

    const custoNum = Number(o.custo);
    o.custo = o.custo === "" || !Number.isFinite(custoNum) ? 0 : custoNum;

    if (!o.ean || String(o.ean).trim() === "") o.ean = "SEM GTIN";

    if (!o.unidade || String(o.unidade).trim() === "") o.unidade = "UN";
    else o.unidade = String(o.unidade).toUpperCase();

    if (!o.ncm || String(o.ncm).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): NCM não pode ser em branco.`);
    }
    if (!o.cfop || String(o.cfop).trim() === "") {
      errors.push(`Linha ${row} (Produto: ${label}): CFOP não pode ser em branco.`);
    }
    if (o.origem == null || String(o.origem).trim() === "") o.origem = 0;

    // ✅ CST (obrigatório) - string e só dígitos
    const cstSan = sanitizeDigitsKeepAsIs(o.cst, "", true);
    if (cstSan === null) {
      errors.push(`Linha ${row} (Produto: ${label}): CST é obrigatório e deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.cst = cstSan;
    }

    const picmsNum = Number(o.picms);
    o.picms = o.picms === "" || !Number.isFinite(picmsNum) ? 0 : picmsNum;

    const predBcNum = Number(o.predBc);
    o.predBc = o.predBc === "" || !Number.isFinite(predBcNum) ? 0 : predBcNum;

    // ✅ CST IPI (opcional) - string e só dígitos (se vier preenchido)
    const cstIpiSan = sanitizeDigitsKeepAsIs(o.cstIpi, "", false);
    if (cstIpiSan === null) {
      errors.push(`Linha ${row} (Produto: ${label}): CSTIPI deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.cstIpi = cstIpiSan; // pode ficar "" se veio vazio
    }

    const pipiNum = Number(o.pipi);
    o.pipi = o.pipi === "" || !Number.isFinite(pipiNum) ? 0 : pipiNum;

    // ✅ CST PIS (obrigatório) - string e só dígitos
    const cstPisSan = sanitizeDigitsKeepAsIs(o.cstPis, "", true);
    if (cstPisSan === null) {
      errors.push(`Linha ${row} (Produto: ${label}): CSTPIS é obrigatório e deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.cstPis = cstPisSan;
    }

    const ppisNum = Number(o.ppis);
    o.ppis = o.ppis === "" || !Number.isFinite(ppisNum) ? 0 : ppisNum;

    // ✅ CST COFINS (obrigatório) - string e só dígitos
    const cstCofinsSan = sanitizeDigitsKeepAsIs(o.cstCofins, "", true);
    if (cstCofinsSan === null) {
      errors.push(`Linha ${row} (Produto: ${label}): CSTCOFINS é obrigatório e deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.cstCofins = cstCofinsSan;
    }

    const pcofinsNum = Number(o.pcofins);
    o.pcofins = o.pcofins === "" || !Number.isFinite(pcofinsNum) ? 0 : pcofinsNum;

    /* ✅ NOVOS CAMPOS */
    o.unidadeTributavel =
      o.unidadeTributavel == null || String(o.unidadeTributavel).trim() === ""
        ? "UN"
        : String(o.unidadeTributavel).toUpperCase();

    // ✅ aceita exatamente como veio (com zeros) e valida apenas dígitos
    const ibs = sanitizeDigitsKeepAsIs(o.ibscbsCst, "000", false);
    if (ibs === null) {
      errors.push(`Linha ${row} (Produto: ${label}): ibscbsCst deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.ibscbsCst = ibs;
    }

    const cclass = sanitizeDigitsKeepAsIs(o.cclassTrib, "000001", false);
    if (cclass === null) {
      errors.push(`Linha ${row} (Produto: ${label}): cclassTrib deve conter apenas dígitos (sem letras/símbolos).`);
    } else {
      o.cclassTrib = cclass;
    }

    const pibsufNum = Number(o.pibsuf);
    o.pibsuf =
      o.pibsuf === "" || o.pibsuf == null || !Number.isFinite(pibsufNum) ? 0.1 : pibsufNum;

    const pibsMunNum = Number(o.pibsMun);
    o.pibsMun =
      o.pibsMun === "" || o.pibsMun == null || !Number.isFinite(pibsMunNum) ? 0 : pibsMunNum;

    const pcbsNum = Number(o.pcbs);
    o.pcbs = o.pcbs === "" || o.pcbs == null || !Number.isFinite(pcbsNum) ? 0.9 : pcbsNum;

    return o;
  });

  return { ajustados, errors };
}

/* ===== transforma no formato do ProdutoInput ===== */
function toProdutoInputPayload(o) {
  return {
    id: o.id || "",
    nome: o.nome,
    descricao: o.descricao,
    preco: o.preco,
    custo: o.custo,
    ativo: true,
    codigo: o.codigo || "",
    ean: o.ean || "SEM GTIN",
    unidade: (o.unidade || "UN").toUpperCase(),

    ncm: o.ncm,
    cfop: o.cfop,
    origem: String(o.origem ?? "0"),

    // ✅ CSTs como string
    cst: o.cst,
    cstIpi: o.cstIpi || "",
    cstPis: o.cstPis,
    cstCofins: o.cstCofins,

    categoria: o.categoria || "geral",

    picms: o.picms,
    predBc: o.predBc,
    pipi: o.pipi,
    ppis: o.ppis,
    pcofins: o.pcofins,

    // ✅ use ?? (não troca 0 por default)
    unidadeTributavel: o?.unidadeTributavel ?? "UN",
    ibscbsCst: o?.ibscbsCst ?? "000", // string
    cclassTrib: o?.cclassTrib ?? "000001", // string
    pibsuf: o?.pibsuf ?? 0.1,
    pibsMun: o?.pibsMun ?? 0,
    pcbs: o?.pcbs ?? 0.9,
  };
}

/* ===== componente ===== */
export default function ImportarProdutos() {
  const accessToken = window.localStorage.getItem("accessToken");
  const [processamentoMsg, setProcessamentoMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: config } = useConfigQuery();
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [executarBusca, setExecutarBusca] = useState(false);

  const onPickFile = () => document.getElementById("xlsx-input")?.click();

  // Valida extensão de arquivo
  const isExcelFile = (f) => {
    if (!f) return false;
    const name = (f.name || "").toLowerCase();
    return name.endsWith(".xlsx") || name.endsWith(".xls");
  };

  useEffect(() => {
    async function fetchData() {
      try {
        let cont = 0;
        while (cont < 6) {
          await new Promise((resolve) => setTimeout(resolve, 10000));
          const { data: result } = await getTransacaoById(transactionId, config.idLoja);

          if (result.status === "2") {
            notification.info({
              message: "Informação",
              description: "As alterações estão sendo processadas.",
            });
          }
          if (result.status === "3") {
            setExecutarBusca(false);
            notification.success({
              message: "Sucesso",
              description: "As alterações foram concluidas.",
            });
            break;
          }
          cont++;
        }

        if (cont === 6) {
          if (window.confirm("Deseja cancelar o processamento?\nClique Ok para cancelar") === true) {
            await cancelarTransacaoPendente(config.idLoja);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (config?.idLoja && transactionId && executarBusca) {
      fetchData();
    }
  }, [config?.idLoja, transactionId, executarBusca]);

  // Lê/processa o arquivo (usado por input e por drop)
  const handleFile = async (file) => {
    setLoading(true);
    setProcessamentoMsg("Processando a planilha aguarde...");

    if (!isExcelFile(file)) {
      notification.error({
        message: "Arquivo inválido",
        description: "Envie um arquivo .xlsx ou .xls.",
      });
      setProcessamentoMsg("");
      setLoading(false);
      return;
    }

    try {
      setFileName(file.name);

      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("Planilha sem aba 1.");

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!aoa.length) throw new Error("Planilha vazia.");
      const headersRaw = aoa[0] || [];

      const idxToKey = {};
      const notMapped = [];
      headersRaw.forEach((h, i) => {
        const keyApi = mapHeaderToKey(h);
        if (keyApi) idxToKey[i] = keyApi;
        else notMapped.push(String(h));
      });

      // ✅ Só cobra as obrigatórias; as novas ficam opcionais
      const expectedRequired = REQUIRED_COLS.map((c) => c.key);
      const found = new Set(Object.values(idxToKey));
      const missing = expectedRequired.filter((k) => !found.has(k));

      if (missing.length) {
        notification.error({
          message: "Colunas faltando ou com nome inesperado",
          description: (
            <div>
              <div>
                Faltando (ou não reconhecidas): <b>{missing.join(", ")}</b>
              </div>
              {notMapped.length > 0 && (
                <div style={{ marginTop: 8 }}>Cabeçalhos não reconhecidos: {notMapped.join(", ")}</div>
              )}
              <div style={{ marginTop: 8, opacity: 0.8 }}>
                Obs.: Os campos novos (unidade_tributavel, ibscbs_cst, cclass_trib, pibsuf, pibsmun, pcbs) são opcionais.
              </div>
              <div style={{ marginTop: 8, opacity: 0.8 }}>
                Obs.: Para preservar zeros à esquerda, deixe <b>cst</b>, <b>cstPis</b>, <b>cstCofins</b>, <b>ibscbs_cst</b> e <b>cclass_trib</b> como <b>TEXTO</b> no Excel.
              </div>
            </div>
          ),
          duration: 12,
        });
        setProcessamentoMsg("");
        setLoading(false);
        return;
      }

      const dataRows = aoa.slice(1).filter((row) => !linhaVaziaByHeaders(row, headersRaw));

      const itensApiPrimarios = dataRows.map((rowArr) => {
        const o = {};
        Object.keys(idxToKey).forEach((idx) => {
          const key = idxToKey[idx];
          o[key] = rowArr[idx];
        });
        return normalizarTiposNumericos(o);
      });

      const { ajustados, errors } = validarEAjustar(itensApiPrimarios);

      if (ajustados.length > 500) {
        notification.error({
          message: "Máximo de 500 produtos por operação",
          duration: 12,
        });
        setProcessamentoMsg("");
        setLoading(false);
        return;
      }

      if (errors.length) {
        const max = 8;
        notification.error({
          message: "Erros de validação",
          description: (
            <div>
              <div>Corrija os itens abaixo e importe novamente:</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.slice(0, max).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
              {errors.length > max && <div>+{errors.length - max} outros erros…</div>}
            </div>
          ),
          duration: 12,
        });
        setProcessamentoMsg("");
        setLoading(false);
        return;
      }

      // ✅ Se seu endpoint espera o payload no formato do DTO, use isso:
      // const payloadCompat = ajustados.map(toProdutoInputPayload);
      // Se seu endpoint já aceita o formato "ajustados" direto, mantenha como está:
      const payload = ajustados; // ou: ajustados.map(toProdutoInputPayload);

      // Debug rápido
      if (payload?.[0]) {
        console.log("Primeiro item importado:", payload[0]);
      }

      notification.success({
        message: "Importação validada",
        description: `Lidas ${ajustados.length} linhas de ${file.name}.`,
      });

      setProcessamentoMsg("Cadastrando ou Atualizando Produtos...");

      const transacao = await gerarTransacao(config.idLoja);
      notification.success({ message: "Transação criada: " + transacao });

      let cont = 0;
      let transactionInfo;

      const options = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      };

      do {
        // ✅ IMPORTANTE: await no PUT
        await axiosInstance.put(
          `${api.techfood}/v1/restaurantes/${config.idLoja}/produtos/planilhaDeProdutos/${transacao}`,
          payload,
          options
        );

        if (config.idLoja && transacao) {
          transactionInfo = await getTransacaoById(transacao, config.idLoja);
          console.log("Transação:", transactionInfo.data);

          if (transactionInfo.data.status !== "1") {
            setTransactionId(transacao);
            setExecutarBusca(true);
            notification.info({
              message: "Informação",
              description: "As alterações estão sendo processadas.",
            });
            break;
          } else {
            await new Promise((resolve) => setTimeout(resolve, 10000));
            cont++;
            console.log(cont + " tentativa");
            if (cont === 6) {
              notification.error({
                message: "Erro",
                description: "Não foi possível o envio das alterações, tente novamente mais tarde.",
              });
            }
          }
        }
      } while (transactionInfo?.data?.status === "1" && cont < 6);

      setProcessamentoMsg("");
      setLoading(false);
      return;
    } catch (error) {
      if (error?.response?.status === 409) {
        notification.info({
          message: "Informação",
          description: "Já existe uma transação ativa para essa loja nos últimos 30 segundos.",
        });
      } else {
        console.error("Erro ao importar:", error);
        notification.error({
          message: "Erro",
          description: "Falha ao processar/importar a planilha. Veja o console para detalhes.",
        });
      }
      setProcessamentoMsg("");
      setLoading(false);
      return;
    }
  };

  // Input file (clique)
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await handleFile(file);
  };

  // Drag & Drop handlers
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setDragging(true);
  };
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (!files || !files.length) return;
    await handleFile(files[0]);
  };

  return (
    <Container>
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 40,
            width: "100%",
          }}
        >
          <Spin
            spinning={loading}
            tip={<span style={{ fontSize: 16, fontWeight: "500", color: "#333" }}>Carregando...</span>}
            size="large"
          />
          {processamentoMsg && (
            <span style={{ marginTop: 16, fontSize: 14, color: "#666" }}>{processamentoMsg}</span>
          )}
        </div>
      ) : null}

      {!loading && (
        <UploadCard>
          <Title level={3}>IMPORTAR PRODUTOS</Title>

          {/* input oculto para clique */}
          <input id="xlsx-input" type="file" accept=".xlsx,.xls" onChange={onFileChange} hidden />

          <Button
            type="primary"
            block
            onClick={onPickFile}
            style={{ backgroundColor: "black", borderColor: "black", color: "#fff" }}
          >
            Selecionar arquivo XLSX
          </Button>

          {/* DROPZONE: clique ou arraste o arquivo */}
          <DropZone
            $dragging={dragging}
            onClick={onPickFile}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPickFile()}
            aria-label="Solte aqui o arquivo XLSX para importar"
          >
            Arraste e solte o arquivo .xlsx aqui
            <div style={{ fontSize: 12, marginTop: 6 }}>ou clique para selecionar</div>
          </DropZone>

          <div style={{ marginTop: 12, color: "#666", fontSize: 12 }}>
            {fileName ? `Selecionado: ${fileName}` : "Use o XLSX no formato do export (id, nome, descricao, preco, ...)."}
          </div>
          <div style={{ marginTop: 12, color: "#666", fontSize: 12 }}>
            {"Máximo de 500 produtos por operação..."}
          </div>

        </UploadCard>
      )}
    </Container>
  );
}
